import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import DeleteConfirmationModal from '../../../components/ui/DeleteConfirmationModal';
import { statementImportsService } from '../../../services/statementImportsService';
import { bankingService } from '../../../services/bankingService';
import { useAuth } from '../../../contexts/AuthContext';
import { cn } from '../../../utils/cn';

const StatementImports = () => {
  const { userProfile, isAuthenticated } = useAuth();
  const [imports, setImports] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState(null);
  const [selectedImport, setSelectedImport] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    bankAccountId: '',
    file: null
  });

  // Get organization ID from authenticated user profile
  const organizationId = userProfile?.organization_id;

  useEffect(() => {
    if (isAuthenticated && organizationId) {
      loadData();
    } else if (isAuthenticated && !organizationId) {
      setError('No organization found. Please contact support.');
      setLoading(false);
    } else {
      setError('Please sign in to access statement imports.');
      setLoading(false);
    }
  }, [isAuthenticated, organizationId]);

  const loadData = async () => {
    if (!organizationId) {
      setError('No organization found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const [importsData, bankAccountsData, statsData] = await Promise.all([
        statementImportsService?.getAll(organizationId),
        bankingService?.getAllBankAccounts(),
        statementImportsService?.getStats(organizationId)
      ]);

      setImports(importsData || []);
      setBankAccounts(bankAccountsData || []);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load statement imports data:', err);
      setError(`Failed to load data: ${err?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    e?.preventDefault();
    
    if (!organizationId) {
      setError('No organization found. Please contact support.');
      return;
    }

    if (!uploadForm?.file || !uploadForm?.bankAccountId) {
      setError('Please select both a file and a bank account');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      // Upload file to storage
      const fileUpload = await statementImportsService?.uploadFile(uploadForm?.file, organizationId);
      
      // Determine file type
      const fileExtension = uploadForm?.file?.name?.split('.')?.pop()?.toLowerCase();
      const fileTypeMap = {
        'csv': 'csv',
        'xls': 'excel',
        'xlsx': 'excel',
        'pdf': 'pdf',
        'qif': 'qif',
        'ofx': 'ofx'
      };

      // Create import record
      await statementImportsService?.create({
        organizationId,
        bankAccountId: uploadForm?.bankAccountId,
        fileName: fileUpload?.fileName,
        filePath: fileUpload?.path,
        fileType: fileTypeMap?.[fileExtension] || 'csv',
        fileSize: fileUpload?.fileSize
      });

      setSuccess('Statement file uploaded successfully and queued for processing');
      setShowUploadForm(false);
      setUploadForm({ bankAccountId: '', file: null });
      await loadData(); // Reload data to show new import
    } catch (err) {
      console.error('Upload failed:', err);
      setError(`Upload failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (importItem) => {
    setSelectedImport(importItem);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await statementImportsService?.delete(selectedImport?.id);
      setSuccess('Statement import deleted successfully');
      await loadData(); // Reload data to reflect deletion
    } catch (err) {
      console.error('Failed to delete import:', err);
      setError(`Failed to delete import: ${err?.message || 'Unknown error'}`);
    } finally {
      setShowDeleteModal(false);
      setSelectedImport(null);
    }
  };

  const downloadFile = async (filePath, fileName) => {
    try {
      const fileUrl = await statementImportsService?.getFileUrl(filePath);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
    } catch (err) {
      console.error('Failed to download file:', err);
      setError(`Failed to download file: ${err?.message || 'Unknown error'}`);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: 'Clock', label: 'Pending' },
      processing: { color: 'bg-blue-100 text-blue-800', icon: 'RefreshCw', label: 'Processing' },
      completed: { color: 'bg-green-100 text-green-800', icon: 'CheckCircle', label: 'Completed' },
      failed: { color: 'bg-red-100 text-red-800', icon: 'XCircle', label: 'Failed' },
      cancelled: { color: 'bg-gray-100 text-gray-800', icon: 'Ban', label: 'Cancelled' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    
    return (
      <span className={cn('inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium', config?.color)}>
        <Icon name={config?.icon} size={12} />
        <span>{config?.label}</span>
      </span>
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  // Show authentication error if not signed in
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Icon name="Lock" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Authentication Required</h3>
          <p className="text-muted-foreground mb-4">
            Please sign in to access statement imports
          </p>
        </div>
      </div>
    );
  }

  // Show organization error if user has no organization
  if (isAuthenticated && !organizationId) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Icon name="Building" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Organization Found</h3>
          <p className="text-muted-foreground mb-4">
            You need to be part of an organization to access statement imports
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3">
          <Icon name="Loader2" size={20} className="animate-spin text-primary" />
          <span className="text-muted-foreground">Loading statement imports...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Statement Imports</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Import bank statements to automatically create transactions
          </p>
        </div>
        <Button
          onClick={() => setShowUploadForm(true)}
          className="flex items-center space-x-2"
          disabled={!bankAccounts?.length}
        >
          <Icon name="Upload" size={16} />
          <span>Import Statement</span>
        </Button>
      </div>
      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-red-600" />
            <span className="text-red-800 text-sm">{error}</span>
          </div>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" size={16} className="text-green-600" />
            <span className="text-green-800 text-sm">{success}</span>
          </div>
        </div>
      )}
      {/* Bank Account Setup Notice */}
      {!bankAccounts?.length && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Icon name="Info" size={16} className="text-blue-600" />
            <span className="text-blue-800 text-sm">
              You need to set up at least one bank account before importing statements. 
              Go to Banking → Bank Accounts to add an account.
            </span>
          </div>
        </div>
      )}
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="bg-surface border border-border rounded-lg p-4">
            <div className="text-2xl font-bold text-foreground">{stats?.total || 0}</div>
            <div className="text-sm text-muted-foreground">Total Imports</div>
          </div>
          <div className="bg-surface border border-border rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats?.pending || 0}</div>
            <div className="text-sm text-muted-foreground">Pending</div>
          </div>
          <div className="bg-surface border border-border rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-600">{stats?.processing || 0}</div>
            <div className="text-sm text-muted-foreground">Processing</div>
          </div>
          <div className="bg-surface border border-border rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">{stats?.completed || 0}</div>
            <div className="text-sm text-muted-foreground">Completed</div>
          </div>
          <div className="bg-surface border border-border rounded-lg p-4">
            <div className="text-2xl font-bold text-red-600">{stats?.failed || 0}</div>
            <div className="text-sm text-muted-foreground">Failed</div>
          </div>
          <div className="bg-surface border border-border rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-600">{stats?.cancelled || 0}</div>
            <div className="text-sm text-muted-foreground">Cancelled</div>
          </div>
        </div>
      )}
      {/* Upload Form Modal */}
      {showUploadForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">Import Statement</h3>
              <button
                onClick={() => {
                  setShowUploadForm(false);
                  setUploadForm({ bankAccountId: '', file: null });
                  setError('');
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            
            <form onSubmit={handleFileUpload} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Bank Account
                </label>
                <Select
                  value={uploadForm?.bankAccountId}
                  onChange={(value) => setUploadForm(prev => ({ ...prev, bankAccountId: value }))}
                  required
                >
                  <option value="">Select Bank Account</option>
                  {bankAccounts?.map(account => (
                    <option key={account?.id} value={account?.id}>
                      {account?.accountName} - {account?.bankName}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Statement File
                </label>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls,.pdf,.qif,.ofx"
                  onChange={(e) => setUploadForm(prev => ({ ...prev, file: e?.target?.files?.[0] }))}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-transparent"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Supported formats: CSV, Excel (.xlsx, .xls), PDF, QIF, OFX (Max 10MB)
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowUploadForm(false);
                    setUploadForm({ bankAccountId: '', file: null });
                    setError('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={uploading}
                  className="flex-1"
                >
                  {uploading ? (
                    <div className="flex items-center space-x-2">
                      <Icon name="Loader2" size={16} className="animate-spin" />
                      <span>Uploading...</span>
                    </div>
                  ) : (
                    'Upload & Import'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Imports Table */}
      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        {imports?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No statement imports yet</h3>
            <p className="text-muted-foreground mb-4">
              Upload your first bank statement to get started with automated transaction imports
            </p>
            {bankAccounts?.length > 0 && (
              <Button
                onClick={() => setShowUploadForm(true)}
                className="flex items-center space-x-2"
              >
                <Icon name="Upload" size={16} />
                <span>Import Your First Statement</span>
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    File & Account
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Transactions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Date Range
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {imports?.map((importItem) => (
                  <tr key={importItem?.id} className="hover:bg-muted/20">
                    <td className="px-6 py-4">
                      <div>
                        <div className="flex items-center space-x-2">
                          <Icon name="FileText" size={16} className="text-muted-foreground" />
                          <span className="font-medium text-foreground">{importItem?.fileName}</span>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {importItem?.bankAccount?.accountName} • {formatFileSize(importItem?.fileSize)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(importItem?.status)}
                      {importItem?.errorMessage && (
                        <div className="text-xs text-red-600 mt-1">
                          {importItem?.errorMessage}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="text-foreground">
                          {importItem?.importedTransactions || 0} / {importItem?.totalTransactions || 0}
                        </div>
                        {importItem?.failedTransactions > 0 && (
                          <div className="text-red-600">
                            {importItem?.failedTransactions} failed
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {importItem?.startDate && importItem?.endDate ? (
                        <div>
                          <div>{new Date(importItem.startDate)?.toLocaleDateString()}</div>
                          <div>to {new Date(importItem.endDate)?.toLocaleDateString()}</div>
                        </div>
                      ) : (
                        'Not processed'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      <div>{new Date(importItem.createdAt)?.toLocaleDateString()}</div>
                      <div className="text-xs">by {importItem?.creator?.fullName}</div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => downloadFile(importItem?.filePath, importItem?.fileName)}
                          className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md"
                          title="Download file"
                        >
                          <Icon name="Download" size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(importItem)}
                          className="p-2 text-muted-foreground hover:text-red-600 hover:bg-muted rounded-md"
                          title="Delete import"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 mb-2">Statement Import Instructions</h4>
            <div className="space-y-2 text-sm text-blue-800">
              <p>• <strong>CSV Files:</strong> Ensure columns are properly labeled (Date, Description, Amount)</p>
              <p>• <strong>Excel Files:</strong> Data should be in the first sheet with headers in row 1</p>
              <p>• <strong>PDF Statements:</strong> Text-based PDFs work best (scanned images may have issues)</p>
              <p>• <strong>Date Formats:</strong> Supported formats include DD/MM/YYYY, MM/DD/YYYY, and YYYY-MM-DD</p>
              <p>• <strong>Processing Time:</strong> Large files may take several minutes to process</p>
            </div>
          </div>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedImport(null);
        }}
        onConfirm={confirmDelete}
        itemName={selectedImport?.fileName}
        itemType="statement import"
        message={`Are you sure you want to delete this statement import? This action cannot be undone.`}
        additionalWarning="All associated transaction data will be permanently removed."
      />
    </div>
  );
};

export default StatementImports;